/**
 * Community — Freeze Dry Academy forum landing page.
 *
 * Modernized take on the classic Discourse two-column layout: category cards
 * on the left (colored left accent, icon, description, monthly topic count),
 * live "Latest" feed on the right. Sub-tab strip switches between
 * Categories · Latest · New · Top · Bookmarks · My Posts; the "+ New Topic"
 * CTA is always in reach.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import ToolSelector from '../components/ToolSelector';
import NewTopicDialog from '../components/community/NewTopicDialog';
import HotTopicsHero from '../components/community/HotTopicsHero';
import { AutoTranslated } from '../components/i18n/AutoTranslated';
import ForumSubNav from '../components/community/ForumSubNav';
import FeedShell from '../components/network/FeedShell';
import PeopleYouMayKnow from '../components/network/PeopleYouMayKnow';
import ProfileSideCard from '../components/network/ProfileSideCard';
import LeftShortcuts from '../components/network/LeftShortcuts';
import CommunityAd from '../components/community/CommunityAd';
import AIAmbassadorPanel from '../components/community/AIAmbassadorPanel';
import DmChatBubble from '../components/network/DmChatBubble';
import ThemePicker from '../components/network/ThemePicker';
import CommunityTour from '../components/CommunityTour';
import { TourLaunchButton } from '../components/PageTour';
import {
  MessageSquare, Sparkles, ChefHat, Cog, Store, Megaphone, GraduationCap,
  Pin, Lock, Bookmark, BookmarkCheck, Plus, Users, TrendingUp, Clock,
  Calendar, BarChart3, Search, Flame, ArrowRight, Zap, BookOpen,
} from 'lucide-react';

const ICON_MAP = {
  MessageSquare, Sparkles, ChefHat, Cog, Store, Megaphone, GraduationCap,
};

const SUB_TABS = [
  { id: 'feed',       label: 'Feed',       icon: Sparkles },
  { id: 'categories', label: 'Categories', icon: MessageSquare },
  { id: 'latest',     label: 'Latest',     icon: Clock },
  { id: 'new',        label: 'New',        icon: Sparkles },
  { id: 'top',        label: 'Top',        icon: Flame },
  { id: 'bookmarks',  label: 'Bookmarks',  icon: Bookmark },
  { id: 'mine',       label: 'My Posts',   icon: Users },
];

const KIND_BADGE = {
  announcement: { label: 'Announcement', className: 'bg-amber-500/15 text-amber-700 border-amber-500/30' },
  poll:         { label: 'Poll',         className: 'bg-violet-500/15 text-violet-700 border-violet-500/30' },
  event:        { label: 'Event',        className: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' },
};

const relTime = (iso) => {
  if (!iso) return '';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ''; }
};

// -------------------------------------------------------------------------
// Category card — left color accent, icon, description, topic count.
// -------------------------------------------------------------------------
const CategoryCard = ({ category, onOpen }) => {
  const Icon = ICON_MAP[category.icon] || MessageSquare;
  return (
    <button
      type="button"
      onClick={() => onOpen(category)}
      data-testid={`community-category-${category.slug}`}
      className="text-left w-full pro-card overflow-hidden hover:-translate-y-0.5 hover:shadow-lg transition-all"
    >
      <div className="flex items-stretch">
        <div className="w-1.5 flex-shrink-0" style={{ backgroundColor: category.color }} />
        <div className="flex-1 p-4">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${category.color}20`, color: category.color }}
              >
                <Icon className="w-4.5 h-4.5" />
              </div>
              <h3 className="font-heading text-base font-extrabold text-foreground truncate">
                {category.name}
              </h3>
              {category.is_lesson_bridge && (
                <span className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground bg-muted px-1.5 py-0.5 rounded flex-shrink-0">
                  Lessons
                </span>
              )}
            </div>
            <span className="text-[11px] font-bold text-muted-foreground whitespace-nowrap">
              {category.topic_count} {category.topic_count === 1 ? 'topic' : 'topics'}
            </span>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">{category.description}</p>
        </div>
      </div>
    </button>
  );
};

// -------------------------------------------------------------------------
// Topic row — used in Latest feed AND category-open lists.
// -------------------------------------------------------------------------
const TopicRow = ({ topic, onOpen }) => {
  const kindMeta = KIND_BADGE[topic.kind];
  const catColor = topic.category?.color || '#0EA5E9';
  return (
    <button
      type="button"
      onClick={() => onOpen(topic)}
      data-testid={`community-topic-${topic.id}`}
      className="w-full text-left rounded-lg border border-transparent hover:border-border hover:bg-card px-3 py-2.5 transition-colors"
    >
      <div className="flex items-start gap-3">
        {/* Author avatar (initials) */}
        <div
          className="mt-0.5 h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
          style={{ backgroundColor: `${catColor}25`, color: catColor }}
          aria-hidden="true"
        >
          {(topic.author?.name || '?').slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {topic.is_pinned && <Pin className="w-3 h-3 text-amber-500 flex-shrink-0" />}
            {topic.is_locked && <Lock className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
            <span className="font-heading text-sm font-bold text-foreground truncate">
              <AutoTranslated as="span" showBadge={false}>{topic.title}</AutoTranslated>
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            {topic.category && (
              <span
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold"
                style={{ backgroundColor: `${catColor}20`, color: catColor }}
              >
                {topic.category.name}
              </span>
            )}
            {kindMeta && (
              <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold border ${kindMeta.className}`}>
                {topic.kind === 'poll' && <BarChart3 className="w-2.5 h-2.5" />}
                {topic.kind === 'event' && <Calendar className="w-2.5 h-2.5" />}
                {topic.kind === 'announcement' && <Megaphone className="w-2.5 h-2.5" />}
                {kindMeta.label}
              </span>
            )}
            {(topic.tags || []).slice(0, 3).map((t) => (
              <span key={t} className="text-[10px] font-semibold text-muted-foreground bg-muted rounded px-1.5 py-0.5">
                {t}
              </span>
            ))}
          </div>
        </div>
        <div className="text-right flex flex-col items-end gap-0.5 flex-shrink-0">
          <div className="text-xs font-bold text-foreground">{topic.reply_count || 0}</div>
          <div className="text-[10px] text-muted-foreground whitespace-nowrap">{relTime(topic.last_activity_at)}</div>
        </div>
      </div>
    </button>
  );
};

// -------------------------------------------------------------------------
// Main community page
// -------------------------------------------------------------------------
export default function Community() {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [latestTopics, setLatestTopics] = useState([]);
  const [freshTopics, setFreshTopics] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(params.get('tab') || 'feed');
  const [activeCategory, setActiveCategory] = useState(params.get('cat') || null);
  const [search, setSearch] = useState('');
  const [newTopicOpen, setNewTopicOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [c, l, a, e, f] = await Promise.all([
          axios.get(`${API}/forum/categories`),
          axios.get(`${API}/forum/topics?sort=latest&limit=15`),
          axios.get(`${API}/forum/announcements?limit=3`),
          axios.get(`${API}/forum/events?upcoming_only=true`),
          axios.get(`${API}/forum/topics?fresh_hours=24&limit=8`),
        ]);
        setCategories(c.data.categories || []);
        setLatestTopics(l.data.topics || []);
        setAnnouncements(a.data.topics || []);
        setEvents(e.data.topics || []);
        setFreshTopics(f.data.topics || []);
      } catch (err) {
        toast.error('Failed to load community');
      } finally { setLoading(false); }
    })();
  }, [API]);

  // Load topic feed whenever tab or category changes.
  useEffect(() => {
    if (tab === 'categories' || tab === 'feed') return;
    (async () => {
      try {
        const q = new URLSearchParams();
        if (activeCategory) q.set('category_id', activeCategory);
        if (tab === 'new') q.set('sort', 'new');
        else if (tab === 'top') q.set('sort', 'top');
        else q.set('sort', 'latest');
        if (tab === 'mine') q.set('mine', '1');
        if (tab === 'bookmarks') q.set('bookmarked', '1');
        const r = await axios.get(`${API}/forum/topics?${q.toString()}`);
        setTopics(r.data.topics || []);
      } catch { setTopics([]); }
    })();
  }, [tab, activeCategory, API]);

  const openTopic = (topic) => {
    if (topic.lesson_id) { navigate(`/lesson/${topic.lesson_id}`); return; }
    navigate(`/community/t/${topic.id}`);
  };

  const openCategory = (cat) => {
    // Navigate to the category's Facebook-Pages-style detail page instead
    // of just filtering the current list.
    navigate(`/community/c/${cat.slug}`);
  };

  const clearCategory = () => {
    setActiveCategory(null);
    setParams({ tab });
  };

  const filteredLatest = useMemo(() => {
    if (!search.trim()) return latestTopics;
    const q = search.toLowerCase();
    return latestTopics.filter((t) => (t.title || '').toLowerCase().includes(q));
  }, [latestTopics, search]);

  const activeCategoryObj = categories.find((c) => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-background">
      <ToolSelector />
      <CommunityTour forceOpen={tourOpen} onFinish={() => setTourOpen(false)} />
      <ForumSubNav onNewTopic={() => setNewTopicOpen(true)} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="eyebrow text-primary">Community</div>
            <h1 className="font-heading text-3xl font-extrabold text-foreground">
              Freeze Dry Academy Forum
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Ask questions, swap recipes, show off your batches, and organize community events. Be kind — everyone was new once.
            </p>
            <Link
              to="/tutorial/community"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline"
              data-testid="community-tutorial-link"
            >
              <BookOpen className="w-3.5 h-3.5" /> New here? Take the community tutorial →
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <ThemePicker />
            <TourLaunchButton onClick={() => setTourOpen(true)} label="Take the on-page tour" testId="community-tour-launch" />
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search topics…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="community-search"
                className="h-9 pl-8 w-56"
              />
            </div>
            <Button
              onClick={() => setNewTopicOpen(true)}
              className="rounded-full font-bold"
              data-testid="community-new-topic-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New topic
            </Button>
          </div>
        </div>

        {/* Announcement rail — always at the top when there's something */}
        {announcements.length > 0 && (
          <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="community-announcement-rail">
            {announcements.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => openTopic(a)}
                className="text-left rounded-xl border border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 px-3 py-2.5 transition-colors"
                data-testid={`community-announcement-${a.id}`}
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1">
                  <Megaphone className="w-3 h-3" /> Announcement
                </div>
                <div className="font-heading text-sm font-bold text-foreground line-clamp-2">{a.title}</div>
                <div className="mt-1 text-[10px] text-muted-foreground">{relTime(a.created_at)}</div>
              </button>
            ))}
          </div>
        )}

        {/* Hot topics hero */}
        <HotTopicsHero />

        {/* Just Posted — brand new topics from the last 24 hours, chronological */}
        {freshTopics.length > 0 && (
          <div className="mt-4 pro-card p-3" data-testid="community-just-posted">
            <div className="flex items-center justify-between mb-2 px-1">
              <div className="inline-flex items-center gap-1.5 eyebrow text-primary">
                <Zap className="w-3.5 h-3.5" /> Just posted
                <span className="text-muted-foreground font-normal normal-case tracking-normal ml-1">
                  · new in the last 24h
                </span>
              </div>
              <button
                type="button"
                onClick={() => { setTab('new'); setParams({ tab: 'new' }); }}
                className="text-[11px] font-bold text-primary hover:underline"
                data-testid="just-posted-view-all"
              >
                View all new →
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {freshTopics.slice(0, 6).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => openTopic(t)}
                  data-testid={`fresh-topic-${t.id}`}
                  className="text-left rounded-xl border border-border hover:border-primary/60 hover:bg-muted/50 px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold mb-1"
                       style={{ color: t.category?.color || '#0EA5E9' }}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: t.category?.color || '#0EA5E9' }}
                          aria-hidden="true" />
                    {t.category?.name || 'Community'}
                  </div>
                  <div className="font-heading text-sm font-bold text-foreground line-clamp-2 leading-tight">
                    {t.title}
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground truncate">
                    {t.author?.name || 'Unknown'} · {relTime(t.created_at)}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* AI Research Ambassador — one-click access from the forum landing */}
        <div className="mt-4">
          <AIAmbassadorPanel
            mode="search"
            title="AI Summarize and Research"
            subtitle="Get a fast AI summary and pointers to threads that answer your question."
            cta="Summarize & Research"
          />
        </div>
        {/* Sub-tabs */}
        <div className="mb-4 flex flex-wrap items-center gap-2 overflow-x-auto no-scrollbar" data-testid="community-subtabs">
          {SUB_TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => { setTab(t.id); setParams({ tab: t.id, ...(activeCategory ? { cat: activeCategory } : {}) }); }}
                data-testid={`community-tab-${t.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full h-8 px-3.5 text-xs font-bold transition-colors flex-shrink-0
                  ${active ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-card border border-border text-foreground/75 hover:text-foreground hover:border-primary/60'}`}
              >
                <t.icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
          {activeCategoryObj && (
            <div className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-xs font-bold bg-foreground text-background" data-testid="community-active-category-chip">
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: activeCategoryObj.color }} />
              {activeCategoryObj.name}
              <button type="button" onClick={clearCategory} className="ml-1 opacity-70 hover:opacity-100" aria-label="Clear category">×</button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)_320px] gap-5">
          {/* LEFT rail — profile card + shortcuts */}
          <aside className="hidden lg:block space-y-3" data-testid="community-left-rail">
            <ProfileSideCard />
            <LeftShortcuts />
          </aside>

          {/* CENTER column */}
          <div className="space-y-3">
            {loading && tab !== 'feed' && (
              <div className="pro-card p-8 text-center text-muted-foreground">Loading community…</div>
            )}

            {tab === 'feed' && (
              <FeedShell scope="for-you" />
            )}

            {!loading && tab === 'categories' && (
              <div className="space-y-3" data-testid="community-category-list">
                {categories.map((c) => (
                  <CategoryCard key={c.id} category={c} onOpen={openCategory} />
                ))}
              </div>
            )}

            {!loading && tab !== 'categories' && tab !== 'feed' && (
              <div className="pro-card p-2" data-testid="community-topic-list">
                {topics.length === 0 ? (
                  <div className="p-6 text-center">
                    <MessageSquare className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-foreground">Nothing here yet</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {tab === 'bookmarks' ? 'Bookmark a topic to save it here.'
                        : tab === 'mine' ? "You haven't posted yet. Start a topic to see it here."
                        : 'Be the first to post in this feed.'}
                    </p>
                  </div>
                ) : topics.map((t) => (
                  <TopicRow key={t.id} topic={t} onOpen={openTopic} />
                ))}
              </div>
            )}
          </div>

          {/* RIGHT rail — Latest + Events */}
          <div className="space-y-4">
            <CommunityAd placement="home" />
            <div className="pro-card p-2">
              <div className="flex items-center justify-between px-2 py-1.5">
                <div className="eyebrow text-muted-foreground">Latest activity</div>
                <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              {filteredLatest.length === 0 ? (
                <div className="p-4 text-center text-xs text-muted-foreground">No topics yet.</div>
              ) : filteredLatest.slice(0, 8).map((t) => (
                <TopicRow key={t.id} topic={t} onOpen={openTopic} />
              ))}
            </div>

            {/* Upcoming events widget */}
            <div className="pro-card p-3" data-testid="community-events-widget">
              <div className="flex items-center justify-between mb-2">
                <div className="eyebrow text-muted-foreground">Upcoming events</div>
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              {events.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Nothing scheduled. <button type="button" onClick={() => setNewTopicOpen(true)} className="text-primary font-bold underline">Add an event</button>.
                </p>
              ) : (
                <div className="space-y-2">
                  {events.slice(0, 4).map((e) => {
                    const d = e.event_starts_at ? new Date(e.event_starts_at) : null;
                    return (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => openTopic(e)}
                        className="w-full text-left rounded-lg hover:bg-muted p-2 transition-colors"
                        data-testid={`community-event-${e.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="mt-0.5 flex flex-col items-center justify-center rounded-md bg-emerald-500/10 text-emerald-700 px-2 py-1 flex-shrink-0 min-w-[42px]">
                            <span className="text-[9px] uppercase font-bold tracking-widest leading-none">
                              {d ? d.toLocaleDateString('en-US', { month: 'short' }) : '—'}
                            </span>
                            <span className="text-sm font-extrabold leading-none">
                              {d ? d.getDate() : '?'}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-bold text-foreground truncate">{e.title}</div>
                            <div className="text-[10px] text-muted-foreground truncate">
                              {d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
                              {e.event_location ? ` · ${e.event_location}` : ''}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Community stats footer */}
            <div className="pro-card p-3">
              <div className="eyebrow text-muted-foreground mb-2">By the numbers</div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="rounded-lg bg-muted p-2">
                  <div className="text-lg font-extrabold text-foreground">{categories.length}</div>
                  <div className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">Categories</div>
                </div>
                <div className="rounded-lg bg-muted p-2">
                  <div className="text-lg font-extrabold text-foreground">
                    {categories.reduce((sum, c) => sum + (c.topic_count || 0), 0)}
                  </div>
                  <div className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">Topics</div>
                </div>
              </div>
              <Link
                to="/graduates"
                className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline"
              >
                See who&apos;s graduated <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {/* Social widget — surfaces on every Community tab */}
            <PeopleYouMayKnow />
          </div>
        </div>
      </div>

      <NewTopicDialog
        open={newTopicOpen}
        onClose={() => setNewTopicOpen(false)}
        categories={categories}
        onCreated={(t) => {
          setNewTopicOpen(false);
          navigate(`/community/t/${t.id}`);
        }}
        defaultCategoryId={activeCategory}
      />

      {/* Floating DM bubble — persistent presence widget */}
      <DmChatBubble />
    </div>
  );
}
